/**
 * @flow
 * @author: Vamsi Tallam
 */

/* eslint-disable no-undef, react/prop-types, react/no-danger */
import React from 'react'
import 'lazysizes'
import Helmet from 'react-helmet'
import { ThemeProvider } from 'emotion-theming'
import ResponsiveContainer from '../components/core/ResponsiveContainer'
import Header from '../components/Header'
import Footer from '../components/core/Footer'
import defaulttheme from '../constants/themeConstants/whiteTheme'
import darkTheme from '../constants/themeConstants/darkTheme'
import { NOT_APPLICABLE } from '../constants/themeConstants'
import { getArticleFormattedDateTime } from '../utils/DateUtils'
import Colors from '../themes/Colors'

import {
  BlogPageContainer,
  MainLayoutContainer,
  HeaderContainer,
  HeaderText,
  CaptionText,
  BodyLayoutContainer,
  DateText,
  Dot,
  ReadingTime,
  ProfileContainer,
  ProfilePic,
  NameContainer,
  NameText,
  DesignationText,
  StatusContainer
} from './styledComponents'
import './styles.scss'

const Template = ({
  data,
  headerPropsData,
  footerProps,
  isBookNowSlotOpened,
  bookNowModalData
}) => {
  if (data) {
    const { blogPageData: { edges: blogPageData } } = data
    const {
      blogTemplateSEOData,
      date,
      readingTime,
      heading,
      caption,
      authorPicUrl,
      authorName,
      authorDesignation,
      html,
      disclaimer
    } = blogPageData[0].node.frontmatter
    const {
      metaTitle,
      metaDescription,
      ogTitle,
      ogUrl,
      ogImage,
      ogDescription,
      ogType,
      ogSiteName,
      twitterTitle,
      twitterUrl,
      twitterSite,
      twitterDescription,
      twitterImage,
      canonicalUrl,
      schemaData
    } = blogTemplateSEOData
    const articleFormattedDate = getArticleFormattedDateTime(date)
    return (
      <div>
        <ThemeProvider theme={defaulttheme}>
          <div>
            <BlogPageContainer className="blogImageContainer">
              <Helmet encodeSpecialCharacters={false}>
                <link
                  href="https://fonts.googleapis.com/css?family=PT+Serif:400,400i,700,700i&display=swap"
                  rel="stylesheet"
                />
                <title>{metaTitle}</title>
                <link rel="canonical" href={canonicalUrl} />
                <meta name="description" content={metaDescription} />
                <meta property="og:title" content={ogTitle} />
                <meta property="og:url" content={ogUrl} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:type" content={ogType} />
                <meta property="og:site_name" content={ogSiteName} />
                <meta property="twitter:title" content={twitterTitle} />
                <meta name="twitter:url" content={twitterUrl} />
                {/* <meta property="twitter:card" content={twittercard} /> */}
                <meta name="twitter:site" content={twitterSite} />
                <meta
                  property="twitter:description"
                  content={twitterDescription}
                />
                <meta name="twitter:image" content={twitterImage} />
                <script type="application/ld+json">{`${schemaData}`}</script>
              </Helmet>
              <Header
                defaultBackground={Colors.darkGreyThree}
                isWhite
                headerPropsData={headerPropsData}
                isBookNowSlotOpened={isBookNowSlotOpened}
                bookNowModalData={bookNowModalData}
              />
              <ResponsiveContainer>
                <MainLayoutContainer>
                  <HeaderContainer>
                    <HeaderText>{heading}</HeaderText>
                    <CaptionText>{caption}</CaptionText>
                  </HeaderContainer>
                  <ProfileContainer>
                    <ProfilePic src={authorPicUrl} />
                    <NameContainer>
                      <NameText>{authorName}</NameText>
                      {authorDesignation !== NOT_APPLICABLE &&
                        authorDesignation &&
                        authorDesignation.trim() && (
                          <DesignationText as="p">
                            {authorDesignation}
                          </DesignationText>
                        )}
                      <StatusContainer>
                        <DateText as="p">{articleFormattedDate}</DateText>
                        <Dot />
                        <ReadingTime>{`${readingTime} read`}</ReadingTime>
                      </StatusContainer>
                    </NameContainer>
                  </ProfileContainer>
                </MainLayoutContainer>
              </ResponsiveContainer>
              <ResponsiveContainer>
                <BodyLayoutContainer>
                  <div
                    className="bodyLayoutContainer"
                    dangerouslySetInnerHTML={{
                      __html: html
                    }}
                  />
                  {disclaimer !== NOT_APPLICABLE &&
                    disclaimer &&
                    disclaimer.trim() && (
                      <p className="Disclaimer">{disclaimer}</p>
                    )}
                  <br />
                </BodyLayoutContainer>
                <br />
                <br />
                <div className="border">&#10070; &#10070; &#10070;</div>
                <br />
                <br />
              </ResponsiveContainer>
            </BlogPageContainer>
            <Footer {...footerProps} theme={darkTheme.footerSection} />
          </div>
        </ThemeProvider>
      </div>
    )
  }
  return null
}

export default Template

// eslint-disable-next-line
export const pageQuery = graphql`
  query blogPageByPath($path: String!) {
    blogPageData: allMarkdownRemark(
      filter: { frontmatter: { path: { eq: $path } } }
    ) {
      edges {
        node {
          frontmatter {
            title
            blogTemplateSEOData {
              metaTitle
              metaDescription
              ogTitle
              ogUrl
              ogImage
              ogDescription
              ogType
              ogSiteName
              twitterTitle
              twitterUrl
              twitterSite
              twitterDescription
              twitterImage
              canonicalUrl
              schemaData
            }
            date
            readingTime
            heading
            caption
            authorPicUrl
            authorName
            authorDesignation
            html
            disclaimer
          }
        }
      }
    }
  }
`



// WEBPACK FOOTER //
// ./src/templates/blog-post-template.js